import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Lock, Clock, Zap, User, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "WebApplication",
      "name": "TempVault",
      "alternateName": "SecureMailWS",
      "url": "https://www.securemailws.com",
      "description": "Free, secure, and private disposable email service. Protect your inbox from spam and keep your personal email address safe.",
      "applicationCategory": "UtilitiesApplication",
      "creator": {
        "@type": "Person",
        "name": "Krish",
        "alternateName": "futureframeai",
        "url": "https://www.securemailws.com/creator",
        "sameAs": [
          "https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
        ]
      },
      "founder": {
        "@type": "Person",
        "name": "Krish",
        "alternateName": "futureframeai",
        "description": "Krish, also known as futureframeai, is the founder and creator of SecureMailWS (TempVault).",
        "url": "https://www.securemailws.com/creator"
      }
    }
  };

  const features = [
    {
      icon: Mail,
      title: 'Instant Temporary Email',
      description: 'Get a disposable email address instantly without any signup or registration.',
    },
    {
      icon: Lock,
      title: 'Privacy First',
      description: 'Your privacy is our priority. No personal information required, ever.',
    },
    {
      icon: Clock,
      title: 'Auto-Expiring',
      description: 'All emails and inboxes automatically expire after 24 hours for maximum security.',
    },
    {
      icon: Zap,
      title: 'Real-Time Updates',
      description: 'Receive emails instantly with real-time inbox updates and notifications.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>About TempVault - Created by Krish (futureframeai) | SecureMailWS</title>
        <meta name="description" content="TempVault (SecureMailWS) is a free, secure disposable email service created by Krish, also known as futureframeai. Learn about our privacy-first approach." />
        <meta name="author" content="Krish (futureframeai)" />
        <meta property="og:title" content="About TempVault - Created by Krish (futureframeai)" />
        <meta property="og:description" content="Learn about TempVault, the privacy-first disposable email service created by Krish (futureframeai)." />
        <link rel="canonical" href="https://www.securemailws.com/about" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen gradient-surface gradient-mesh flex flex-col">
        <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <div className="glass-strong rounded-3xl p-6 lg:p-10 glow-border">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="w-12 h-12 rounded-2xl gradient-bg flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold gradient-text">About TempVault</h1>
          </div>

          <div className="space-y-8">
            <section>
              <p className="text-lg text-muted-foreground leading-relaxed">
                TempVault is a free, secure disposable email service designed to protect your privacy online. 
                Whether you're signing up for a new service, avoiding spam, or just want to keep your real 
                email address private, TempVault has you covered.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6">Why Choose TempVault?</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {features.map((feature) => (
                  <div
                    key={feature.title}
                    className="glass rounded-2xl p-5 hover-lift transition-all duration-300"
                  >
                    <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center mb-4">
                      <feature.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">How It Works</h2>
              <div className="space-y-4 text-muted-foreground">
                <p className="leading-relaxed">
                  <span className="font-semibold text-foreground">1. Get Your Address:</span> When you visit TempVault, 
                  we instantly generate a unique temporary email address just for you.
                </p>
                <p className="leading-relaxed">
                  <span className="font-semibold text-foreground">2. Use It Anywhere:</span> Use this address to sign up 
                  for newsletters, verify accounts, or anywhere you need an email.
                </p>
                <p className="leading-relaxed">
                  <span className="font-semibold text-foreground">3. Receive Emails:</span> All emails sent to your 
                  temporary address appear in your inbox in real-time.
                </p>
                <p className="leading-relaxed">
                  <span className="font-semibold text-foreground">4. Auto-Delete:</span> After 24 hours, everything is 
                  automatically deleted. No traces, no worries.
                </p>
              </div>
            </section>
            {/* Founder Section */}
            <section>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <User className="w-6 h-6 text-primary" />
                Founder & Creator
              </h2>
              <Link 
                to="/creator"
                className="block glass rounded-2xl p-6 hover-lift transition-all duration-300 group"
              >
                <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
                  <div className="w-16 h-16 rounded-full gradient-bg flex items-center justify-center flex-shrink-0">
                    <User className="w-8 h-8 text-primary-foreground" />
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl font-bold group-hover:text-primary transition-colors">Krish</h3>
                    <p className="text-primary font-medium mb-2">@futureframeai</p>
                    <p className="text-sm text-muted-foreground">
                      Creator and developer of TempVault (SecureMailWS). Passionate about building 
                      privacy-focused tools and AI-powered applications.
                    </p>
                    <div className="flex items-center gap-2 mt-3 justify-center sm:justify-start">
                      <Youtube className="w-4 h-4 text-destructive" />
                      <span className="text-sm text-muted-foreground">Follow on YouTube</span>
                    </div>
                  </div>
                </div>
              </Link>
            </section>

            <section className="glass rounded-2xl p-6 text-center">
              <h2 className="text-xl font-bold mb-3">Ready to Protect Your Privacy?</h2>
              <p className="text-muted-foreground mb-4">
                Start using TempVault today - it's completely free!
              </p>
              <Link to="/">
                <Button className="rounded-full px-8">
                  Get Started
                </Button>
              </Link>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
    </>
  );
};

export default About;
